import { useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, BadgeCheck, Camera, Copy, KeyRound, Mail, MapPin, Phone, Power, Timer, UserCog,
} from 'lucide-react';
import {
  EMPLOYEE_STATUS, METRIC_DEFINITIONS, WORK_STATUS, dateLabel, hours, money,
  type EmployeeStatus, type WorkStatus,
} from '@adisys/shared';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Avatar } from '../components/Avatar';
import { PageHeader } from '../components/AppShell';
import {
  Button, Card, CardHeader, ErrorState, InfoTip, Modal, Select, Skeleton, StatusBadge,
  Tabs, Textarea, Field, cx, useToast,
} from '../components/ui';

export function EmployeeDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { can, user } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const [tab, setTab] = useState<'work' | 'projects' | 'expenses'>('work');
  const [statusOpen, setStatusOpen] = useState(false);
  const [credentials, setCredentials] = useState<string | null>(null);

  const query = useQuery({ queryKey: ['employee', id], queryFn: () => api.get(`/employees/${id}`) });
  const e = query.data?.employee;

  const expenses = useQuery({
    queryKey: ['employee', id, 'expenses'],
    queryFn: () => api.get('/expenses', { userId: id, size: 15 }),
    enabled: tab === 'expenses',
  });

  /* --- Profile photo -------------------------------------------------
     Same rule the API enforces: anyone with employee.update may change
     another person's photo, and only a Super Admin may touch a Super
     Admin's. Editing your own is always allowed. */
  const photoRef = useRef<HTMLInputElement>(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const PHOTO_ACCEPT = ['image/jpeg', 'image/png', 'image/webp'];
  const PHOTO_MAX_MB = 10;
  const mayEditPhoto = Boolean(e) && (
    e.id === user?.id ||
    (can('employee.update') && (e.roleKey !== 'super_admin' || user?.roleKey === 'super_admin'))
  );

  const afterPhoto = async () => {
    await qc.invalidateQueries({ queryKey: ['employee', id] });
    void qc.invalidateQueries({ queryKey: ['employees'] });
  };

  const choosePhoto = async (file: File | undefined) => {
    if (!file) return;
    if (!PHOTO_ACCEPT.includes(file.type)) {
      toast.error('Unsupported image', 'Use a JPG, PNG or WEBP file.');
      return;
    }
    if (file.size > PHOTO_MAX_MB * 1024 * 1024) {
      toast.error('Image too large', `Photos must be ${PHOTO_MAX_MB} MB or smaller.`);
      return;
    }
    const body = new FormData();
    body.append('avatar', file);
    setPhotoBusy(true);
    try {
      await api.upload(`/employees/${id}/avatar`, body);
      await afterPhoto();
      toast.success('Photo updated');
    } catch (err) {
      toast.error('Could not upload the photo', (err as Error).message);
    } finally {
      setPhotoBusy(false);
      if (photoRef.current) photoRef.current.value = '';
    }
  };

  const removePhoto = async () => {
    setPhotoBusy(true);
    try {
      await api.del(`/employees/${id}/avatar`);
      await afterPhoto();
      toast.success('Photo removed');
    } catch (err) {
      toast.error('Could not remove the photo', (err as Error).message);
    } finally {
      setPhotoBusy(false);
    }
  };

  const resetCredentials = useMutation({
    mutationFn: () => api.post(`/employees/${id}/reset-credentials`),
    onSuccess: (res) => {
      setCredentials(res.temporaryPassword);
      toast.success('New password issued', 'All existing sessions were signed out.');
    },
    onError: (err) => toast.error('Could not reset the password', (err as Error).message),
  });

  if (query.isError) {
    return (
      <>
        <PageHeader title="Employee" />
        <div className="p-6"><Card><ErrorState error={query.error} onRetry={() => query.refetch()} /></Card></div>
      </>
    );
  }

  const statusMeta = e ? EMPLOYEE_STATUS.byValue[e.status as EmployeeStatus] : null;
  const p30 = query.data?.productivity30d;
  const x90 = query.data?.expenses90d;

  return (
    <>
      <PageHeader
        title={e?.fullName ?? 'Employee'}
        description={e ? `${e.employeeCode}${e.designationName ? ` · ${e.designationName}` : ''}${e.departmentName ? ` · ${e.departmentName}` : ''}` : undefined}
        actions={
          <>
            <Button icon={<ArrowLeft className="h-4 w-4" />} onClick={() => navigate('/employees')}>Back</Button>
            {can('employee.update') && e && e.id !== user?.id && (
              <Button icon={<KeyRound className="h-4 w-4" />} loading={resetCredentials.isPending}
                      onClick={() => resetCredentials.mutate()}>
                Issue new password
              </Button>
            )}
            {can('employee.deactivate') && e && e.id !== user?.id && (
              <Button variant={e.status === 'active' ? 'danger' : 'success'}
                      icon={<Power className="h-4 w-4" />} onClick={() => setStatusOpen(true)}>
                {e.status === 'active' ? 'Deactivate' : 'Activate'}
              </Button>
            )}
          </>
        }
      />

      <div className="grid gap-5 p-4 sm:p-6 xl:grid-cols-[340px_minmax(0,1fr)]">
        {/* --- Profile ------------------------------------------- */}
        <div className="space-y-5">
          <Card>
            {query.isLoading ? (
              <div className="space-y-3"><Skeleton className="h-16 w-16 rounded-full" /><Skeleton className="h-4 w-32" /></div>
            ) : e && (
              <>
                <div className="flex items-center gap-3">
                  <span className="relative shrink-0">
                    <Avatar name={e.fullName} fileId={e.avatarFileId} size={56}
                            className={photoBusy ? 'opacity-50' : undefined} />
                    {/* On duty moves to the top so the edit control can own the
                        bottom corner without either signal displacing the other. */}
                    {e.onDuty && (
                      <span title="On duty"
                        className="absolute -right-0.5 -top-0.5 h-3.5 w-3.5 rounded-full bg-success ring-2 ring-card" />
                    )}
                    {mayEditPhoto && (
                      <button type="button" disabled={photoBusy}
                        onClick={() => photoRef.current?.click()}
                        title="Edit photo" aria-label={`Edit ${e.fullName}'s photo`}
                        className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center
                                   rounded-full bg-brand-500 text-white ring-2 ring-card hover:brightness-110">
                        <Camera className="h-3.5 w-3.5" aria-hidden />
                      </button>
                    )}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-ink-900">{e.fullName}</p>
                    <p className="tabular text-xs text-ink-500">{e.employeeCode}</p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {statusMeta && <StatusBadge tone={statusMeta.tone}>{statusMeta.label}</StatusBadge>}
                      {e.onLeave && (
                        <StatusBadge tone="info" title={`On approved ${e.onLeaveType ?? 'leave'} today — not absent`}>
                          On Leave
                        </StatusBadge>
                      )}
                      {e.onDuty && <StatusBadge tone="success">On duty</StatusBadge>}
                      {e.timerRunning && <StatusBadge tone="progress">Timer running</StatusBadge>}
                    </div>
                  </div>
                </div>

                {mayEditPhoto && (
                  <div className="mt-3 flex items-center gap-3 text-xs">
                    <button type="button" disabled={photoBusy} onClick={() => photoRef.current?.click()}
                      className="font-medium text-ink-600 hover:text-ink-900 disabled:opacity-60">
                      {e.avatarFileId ? 'Replace photo' : 'Add a photo'}
                    </button>
                    {e.avatarFileId && (
                      <button type="button" disabled={photoBusy} onClick={() => void removePhoto()}
                        className="font-medium text-danger hover:brightness-110 disabled:opacity-60">
                        Remove photo
                      </button>
                    )}
                    <input ref={photoRef} type="file" accept={PHOTO_ACCEPT.join(',')} className="sr-only"
                           onChange={(ev) => void choosePhoto(ev.target.files?.[0])} />
                  </div>
                )}

                <dl className="mt-5 space-y-3 border-t border-line pt-4 text-sm">
                  <Row icon={<Mail className="h-3.5 w-3.5" />} term="Email" value={e.email ?? 'None on record'} />
                  <Row icon={<Phone className="h-3.5 w-3.5" />} term="Mobile" value={e.phone} />
                  <Row icon={<UserCog className="h-3.5 w-3.5" />} term="Role" value={e.roleName} />
                  <Row icon={<MapPin className="h-3.5 w-3.5" />} term="Base location" value={e.baseLocationName ?? '—'} />
                  <Row term="Reports to" value={e.reportingManagerName ?? 'No manager set'} />
                  <Row term="Joined" value={dateLabel(e.dateOfJoining)} />
                  <Row term="Last sign-in" value={e.lastLoginAt ? dateLabel(e.lastLoginAt) : 'Never'} />
                </dl>
              </>
            )}
          </Card>

          <AttendanceLocations
            rows={query.data?.attendance ?? []}
            visible={query.data?.locationVisible ?? false}
            name={e?.fullName ?? ''} consentedAt={e?.locationConsentAt} />

          <Card>
            <CardHeader title="Last 30 days" subtitle="Time recorded and verified" />
            {p30 && (
              <dl className="space-y-2.5">
                <Metric label="Recorded against work" value={hours(p30.recordedMinutes)}
                        definition={METRIC_DEFINITIONS.recorded_hours} />
                <Metric label="Verified productive hours" value={hours(p30.verifiedMinutes)} tone="success"
                        definition={METRIC_DEFINITIONS.verified_hours} />
                <Metric label="Logged without an assignment" value={hours(p30.unassignedMinutes)}
                        tone={Number(p30.unassignedMinutes) > 0 ? 'warning' : undefined}
                        definition={METRIC_DEFINITIONS.unassigned_hours} />
              </dl>
            )}
          </Card>

          <Card>
            <CardHeader title="Last 90 days" subtitle="Expense claims" />
            {x90 && (
              <dl className="space-y-2.5">
                <Metric label="Claims submitted" value={String(x90.count)} />
                <Metric label="Approved value" value={money(x90.approved)} tone="success" />
                <Metric label="Awaiting decision" value={money(x90.pending)}
                        tone={Number(x90.pending) > 0 ? 'warning' : undefined} />
              </dl>
            )}
          </Card>
        </div>

        {/* --- Activity ------------------------------------------ */}
        <div className="space-y-4">
          <Tabs
            tabs={[
              { key: 'work', label: 'Recent work', count: query.data?.recentWork?.length },
              { key: 'projects', label: 'Projects', count: query.data?.projects?.length },
              { key: 'expenses', label: 'Expenses' },
            ]}
            active={tab} onChange={setTab} />

          {tab === 'work' && (
            <Card padded={false}>
              {(query.data?.recentWork ?? []).length === 0 ? (
                <p className="px-5 py-10 text-center text-sm text-ink-500">No work has been assigned yet.</p>
              ) : (
                <ul className="divide-y divide-line">
                  {query.data.recentWork.map((w: any) => {
                    const meta = WORK_STATUS.byValue[w.status as WorkStatus];
                    return (
                      <li key={w.id} className="flex items-center gap-3 px-5 py-3">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-ink-800">{w.title}</p>
                          <p className="tabular truncate text-xs text-ink-500">
                            {w.assignmentCode} · {w.projectName} · due {dateLabel(w.dueDate)}
                          </p>
                        </div>
                        <StatusBadge tone={meta.tone} title={meta.description}>{meta.label}</StatusBadge>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>
          )}

          {tab === 'projects' && (
            <Card padded={false}>
              {(query.data?.projects ?? []).length === 0 ? (
                <p className="px-5 py-10 text-center text-sm text-ink-500">
                  Not assigned to any project. They cannot be given work or submit claims until they are.
                </p>
              ) : (
                <ul className="divide-y divide-line">
                  {query.data.projects.map((p: any) => (
                    <li key={p.projectId}>
                      <Link to={`/projects/${p.projectId}`} className="flex items-center gap-3 px-5 py-3 hover:bg-ink-50">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-ink-800">{p.name}</p>
                          <p className="tabular truncate text-xs text-ink-500">
                            {p.projectCode} · {p.roleInProject}
                            {p.allocationPct ? ` · ${p.allocationPct}% allocated` : ''}
                          </p>
                        </div>
                        <StatusBadge tone={p.status === 'active' ? 'success' : 'neutral'}>{p.status}</StatusBadge>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}

          {tab === 'expenses' && (
            <Card padded={false}>
              {expenses.isLoading ? (
                <div className="space-y-2 p-5">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
              ) : (expenses.data?.data ?? []).length === 0 ? (
                <p className="px-5 py-10 text-center text-sm text-ink-500">No expense claims in the period.</p>
              ) : (
                <ul className="divide-y divide-line">
                  {expenses.data.data.map((c: any) => (
                    <li key={c.id}>
                      <Link to={`/expenses/${c.id}`} className="flex items-center gap-3 px-5 py-3 hover:bg-ink-50">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-ink-800">
                            {money(c.amount)} · {c.categoryName}
                          </p>
                          <p className="tabular truncate text-xs text-ink-500">
                            {c.expenseCode} · {dateLabel(c.expenseDate)}
                          </p>
                        </div>
                        <StatusBadge tone={c.status === 'approved' || c.status === 'paid' ? 'success'
                          : c.status === 'rejected' ? 'danger' : 'warning'}>{c.status.replace('_', ' ')}</StatusBadge>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}
        </div>
      </div>

      {statusOpen && e && (
        <StatusModal employee={e} onClose={() => setStatusOpen(false)}
          onDone={() => {
            setStatusOpen(false);
            void qc.invalidateQueries({ queryKey: ['employee', id] });
            void qc.invalidateQueries({ queryKey: ['employees'] });
          }} />
      )}

      {credentials && (
        <Modal open onClose={() => setCredentials(null)} size="sm" title="New temporary password"
          description="Share this over a secure channel. It is shown once."
          footer={<Button variant="primary" onClick={() => setCredentials(null)}>Done</Button>}>
          <div className="flex items-center justify-between gap-2 rounded-lg bg-sunken p-3.5">
            <span className="font-mono text-lg font-semibold text-ink-900">{credentials}</span>
            <button onClick={() => { void navigator.clipboard?.writeText(credentials); toast.info('Copied'); }}
                    className="rounded p-1 text-ink-400 hover:bg-white hover:text-ink-700" aria-label="Copy">
              <Copy className="h-4 w-4" />
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}

/* =================================================================== */
function StatusModal({ employee, onClose, onDone }: { employee: any; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [status, setStatus] = useState(employee.status === 'active' ? 'inactive' : 'active');
  const [reason, setReason] = useState('');

  const save = useMutation({
    mutationFn: () => api.post(`/employees/${employee.id}/status`, { status, reason: reason || undefined }),
    onSuccess: () => {
      toast.success(`${employee.fullName} is now ${status}`,
        status !== 'active' ? 'All their sessions have been signed out.' : 'They can sign in again.');
      onDone();
    },
    onError: (err) => toast.error('Could not change the status', (err as Error).message),
  });

  return (
    <Modal open onClose={onClose} size="sm" title={`Change status — ${employee.fullName}`}
      description="Deactivating signs the employee out of every device immediately."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant={status === 'active' ? 'success' : 'danger'} loading={save.isPending}
                  onClick={() => save.mutate()}>Apply</Button>
        </>
      }>
      <div className="space-y-4">
        <Field label="New status" required>
          <Select value={status} onChange={(e) => setStatus(e.target.value)}>
            {EMPLOYEE_STATUS.list.filter((s) => s.value !== 'invited').map((s) => (
              <option key={s.value} value={s.value}>{s.label} — {s.description}</option>
            ))}
          </Select>
        </Field>
        <Field label="Reason" hint="Recorded in the audit log.">
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        {employee.openAssignmentCount > 0 && status !== 'active' && (
          <p className="rounded-lg bg-warning-soft px-3 py-2.5 text-xs leading-relaxed text-warning ring-1 ring-inset ring-warning/20">
            This employee still has {employee.openAssignmentCount} open assignment(s). Reassign or cancel
            them first — the change will be refused otherwise.
          </p>
        )}
      </div>
    </Modal>
  );
}

/**
 * Where this employee checked in and out, most recent first.
 *
 * These coordinates were being written at check-in and check-out all
 * along, but nothing ever read them back, so the whole record was
 * invisible. Each position links out to a map rather than embedding one:
 * a map tile provider would send the employee's position to a third
 * party on every page view, which is not a reasonable default for staff
 * location data.
 */
function AttendanceLocations({ rows, visible, name, consentedAt }: {
  rows: any[]; visible: boolean; name: string; consentedAt?: string | null;
}) {
  if (!rows.length) {
    return (
      <Card>
        <CardHeader title="Check-in locations" subtitle="Recorded at the start and end of each day" />
        <p className="text-sm text-ink-500">No attendance recorded yet.</p>
      </Card>
    );
  }

  const anyRecorded = rows.some((r) => r.checkInLocationRecorded || r.checkOutLocationRecorded);
  const consented = Boolean(consentedAt);

  return (
    <Card>
      <CardHeader title="Check-in locations" subtitle="Recorded at the start and end of each day" />

      {!visible ? (
        <p className="rounded-lg bg-sunken px-3 py-2.5 text-sm leading-relaxed text-ink-600">
          You can see that {name.split(' ')[0] || 'this employee'} checked in, but not where.
          Positions are limited to their reporting manager and an administrator.
        </p>
      ) : !consented ? (
        <p className="rounded-lg bg-warning-soft px-3 py-2.5 text-sm leading-relaxed text-ink-700 ring-1 ring-inset ring-warning/25">
          Location sharing is off for this employee, so nothing is recorded.
          They can turn it on in the field app, under Profile.
        </p>
      ) : !anyRecorded ? (
        <p className="rounded-lg bg-sunken px-3 py-2.5 text-sm leading-relaxed text-ink-600">
          Location sharing is on, but no fix has been obtained yet — a phone indoors or
          underground often cannot get one. Attendance itself is unaffected.
        </p>
      ) : null}

      <ul className={cx('divide-y divide-line', (!visible || !consented || !anyRecorded) && 'mt-3')}>
        {rows.map((r) => (
          <li key={r.workDate} className="py-2.5">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="text-sm font-medium text-ink-900">{dateLabel(r.workDate)}</span>
              <span className="tabular text-xs text-ink-500">
                {timeOf(r.checkInAt)} → {r.checkOutAt ? timeOf(r.checkOutAt) : 'still on duty'}
                {r.durationMinutes != null && ` · ${hours(r.durationMinutes)}`}
              </span>
            </div>
            {visible && (
              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs">
                <Fix label="In" lat={r.checkInLatitude} lng={r.checkInLongitude}
                     recorded={r.checkInLocationRecorded} accuracyM={r.accuracyM} />
                <Fix label="Out" lat={r.checkOutLatitude} lng={r.checkOutLongitude}
                     recorded={r.checkOutLocationRecorded} accuracyM={r.accuracyM}
                     pending={!r.checkOutAt} />
              </div>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

const timeOf = (iso?: string | null) =>
  (iso ? new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '—');

/** One recorded position, or an honest reason there isn't one. */
function Fix({ label, lat, lng, recorded, accuracyM, pending }: {
  label: string; lat?: string | number | null; lng?: string | number | null;
  recorded?: boolean; accuracyM?: string | number | null; pending?: boolean;
}) {
  if (pending) return <span className="text-ink-400">{label}: —</span>;
  if (!recorded || lat == null || lng == null) {
    return <span className="text-ink-400">{label}: no fix</span>;
  }
  const a = Number(lat).toFixed(6);
  const o = Number(lng).toFixed(6);
  return (
    <span className="flex items-center gap-1">
      <span className="text-ink-500">{label}:</span>
      <a href={`https://www.google.com/maps/search/?api=1&query=${a},${o}`}
         target="_blank" rel="noopener noreferrer"
         className="tabular font-medium text-info underline-offset-2 hover:underline">
        {a}, {o}
      </a>
      {accuracyM != null && <span className="text-ink-400">±{Math.round(Number(accuracyM))}m</span>}
    </span>
  );
}

function Row({ icon, term, value }: { icon?: React.ReactNode; term: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="flex items-center gap-1.5 shrink-0 text-xs text-ink-500">{icon}{term}</dt>
      <dd className="truncate text-right text-sm text-ink-800">{value}</dd>
    </div>
  );
}

function Metric({ label, value, tone, definition }: {
  label: string; value: string; tone?: 'success' | 'warning'; definition?: string;
}) {
  const colour = tone === 'success' ? 'text-success' : tone === 'warning' ? 'text-warning' : 'text-ink-900';
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="flex items-center gap-1 text-xs text-ink-500">
        {label}{definition && <InfoTip text={definition} />}
      </dt>
      <dd className={`tabular text-sm font-semibold ${colour}`}>{value}</dd>
    </div>
  );
}
